import React from 'react';

// components
import { Link } from 'src/components/Inputs';
import { EmbeddedForeignContentBanner } from 'src/components/Feedback';

// helpers
import { useUserLocale } from 'src/hooks';
import { Locales } from 'src/lib/constants';

const renderForeignContentBanner = (localeCode) => {
  switch (localeCode) {
    case 'en-us':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_GB}
          href='#end-user-services-agreement-us'
        />
      );
    case 'en-eu':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_GB}
          href='#end-user-services-agreement-eea'
        />
      );
    default:
      return null;
  }
};

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const UKEndUserServicesAgreement = () => {
  const { locale: userLocale } = useUserLocale();
  return (
    <>
      <h4
        id='end-user-services-agreement-uk'
        data-magellan-target='end-user-services-agreement-uk'
      >
        End User Services Agreement (UK)
      </h4>
      {renderForeignContentBanner(userLocale.code)}
      <p>
        <em>Last Updated: 04 August 2022</em>
      </p>
      <p>Thank you for using Plaid!</p>
      <p>
        This End User Services Agreement (&quot;
        <strong>Agreement</strong>&quot;) is a legal agreement between Plaid
        Financial Ltd. (&quot;<strong>Plaid</strong>&quot;, &quot;
        <strong>we</strong>&quot; or &quot;<strong>us</strong>&quot;) and the
        end user of our Services (&quot;<strong>you</strong>
        &quot;, &quot;<strong>your</strong>&quot;). This Agreement only applies
        when we provide account information services and payment initiation
        services to you with respect to a UK payment account, and when we
        transmit retrieved data and/or the payment status related to such
        services to third parties on your behalf (&quot;
        <strong>Services</strong>
        &quot;). Such third parties include the owner or provider of the
        website, desktop and/or mobile application through which you have
        accessed our Services (&quot;<strong>Application</strong>&quot;).
      </p>
      <p>
        This Agreement describes the terms and conditions that apply to your use
        of the Services. You may not access or use the Services unless you agree
        to comply with all of the terms and conditions in this Agreement. You
        should therefore read this Agreement carefully and make sure you
        understand it. If you do not understand any of the terms and conditions
        of this Agreement, please contact us before using the Services by
        emailing us at{' '}
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        You may view an up-to-date copy of this Agreement at any time at{' '}
        <Link href='https://plaid.com/legal/#end-user-services-agreement-uk'>
          <a>https://plaid.com/legal/#end-user-services-agreement-uk</a>
        </Link>
        .
      </p>
      <p>
        <em>Jump to section:</em>
      </p>
      <p>
        <Link href='#plaid-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Plaid
          </a>
        </Link>
      </p>

      <p>
        <Link href='#plaid-services-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Plaid Services
          </a>
        </Link>
      </p>

      <p>
        <Link href='#eligibility-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Eligibility and Availability
          </a>
        </Link>
      </p>

      <p>
        <Link href='#communication-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Communication
          </a>
        </Link>
      </p>

      <p>
        <Link href='#incorrect-unauth-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Incorrect or Unauthorised Payments
          </a>
        </Link>
      </p>

      <p>
        <Link href='#data-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Data
          </a>
        </Link>
      </p>

      <p>
        <Link href='#liability-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Liability
          </a>
        </Link>
      </p>

      <p>
        <Link href='#complaints-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Complaints
          </a>
        </Link>
      </p>

      <p>
        <Link href='#changes-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Changes to this Agreement
          </a>
        </Link>
      </p>

      <p>
        <Link href='#termination-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Termination
          </a>
        </Link>
      </p>

      <p>
        <Link href='#law-end-user-uk'>
          <a data-smooth-scroll data-offset='80'>
            Governing law and language
          </a>
        </Link>
      </p>

      <p id='plaid-end-user-uk'>
        <strong>1. Plaid</strong>
      </p>
      <p>
        1.1. Plaid Financial Ltd. (company registration number 11103959) is
        authorised by the Financial Conduct Authority under the Payment Services
        Regulations 2017 for the provision of payment initiation and account
        information services (firm reference number 804718).
      </p>
      <p>
        1.2. Plaid&#39;s registered office address is New Penderel House, 4th
        Floor, 283-288 High Holborn, London WC1V 7HP. Plaid&#39;s trading
        address is 35-41 Folgate Street, London, E1 6BX.
      </p>

      <p id='plaid-services-end-user-uk'>
        <strong>2. Plaid Services</strong>
      </p>
      <p>
        2.1. You have accessed our Services through an Application or within the
        Plaid dashboard (available at{' '}
        <Link href='https://my.plaid.com/'>
          <a>https://my.plaid.com/</a>
        </Link>{' '}
        ). The products and services provided to you by the Application are
        governed by a separate agreement between you and the provider of the
        Application (&quot;
        <strong>Application Terms</strong>
        &quot;). We have no responsibility for the products and services
        provided to you by or through the Application and will not be liable to
        you for any harm, damage or loss arising from your use of the products
        and services provided by or through the Application.
      </p>
      <p>
        2.2. Our Services will allow you to obtain financial information from
        your online bank or payment account (&quot;
        <strong>Payment Account</strong>&quot;) (account information services)
        and to make online payments directly from your Payment Account (payment
        initiation services).
      </p>
      <p>
        2.3. The terms and conditions that apply to the Payment Accounts that
        you access through our Services (the &quot;
        <strong>Account Terms</strong>&quot;) will remain in effect and this
        Agreement does not change your Account Terms.
      </p>
      <p>
        2.4. We will not charge you for the use of our Services. Applications or
        other third parties may, however, charge you for products and services
        provided to you that make use of the Services provided by Plaid under
        this Agreement. Application providers and other third parties may pay us
        fees and other amounts in connection with the services we provide to
        them.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>
          Account information services
        </span>
      </p>
      <p>
        2.5. Plaid&#39;s account information services allow you to access and
        view information relating to your selected Payment Account within the
        Plaid dashboard (available at{' '}
        <Link href='https://my.plaid.com/'>
          <a>https://my.plaid.com/</a>
        </Link>{' '}
        ) and the Application through which you have accessed our Services.
      </p>
      <p>
        2.6. To access the Plaid dashboard, you need to create an account with
        Plaid. You also need to ensure that your information is accurate,
        complete and up-to-date. You must follow best practices to help secure
        your Plaid account and notify us if you learn of any unauthorised access
        to or use of your Plaid account.
      </p>
      <p>
        2.7. With your explicit consent, we access and display information
        relating to your selected Payment Account(s) (&quot;
        <strong>Account Information</strong>&quot;) within the Plaid dashboard
        and transmit such information to the Application through which you have
        accessed our Services. Such information may include: a. financial
        transaction history, for example, transaction amount, data, description
        and currency; b. financial account details, for example, account number,
        type, currency and balance; and c. financial account holder information,
        for example, name, address, phone number, and email address.
      </p>
      <p>
        2.8. Before receiving Plaid&#39;s account information service, you will
        have instructed the Application through which you have accessed our
        Services to retrieve your Account Information using Plaid. Upon such an
        instruction, you will generally be redirected to us.
      </p>
      <p>
        2.9. Once you are redirected to us, we will ask you to select which
        Payment Account provider (&quot;<strong>Account</strong>{' '}
        <strong>Provider</strong>&quot;) you would like us to access Account
        Information from. You will give your explicit consent to us: (i)
        accessing your Account Information from the Account Provider you have
        selected; and (ii) taking the steps outlined in clause paragraph 2.12.
        once we have accessed your Account Information. Plaid will request your
        Account Information from your Account Provider on a periodic basis in
        accordance with your consent until the consent expires or is withdrawn.
      </p>
      <p>
        2.10. Please note, you may be redirected to your Account Provider&#39;s
        website or mobile application in order to authenticate yourself so that
        your Account Provider knows that you consent to us accessing your
        Account Information.
      </p>
      <p>
        2.11. Alternatively, in some circumstances, we may ask you to provide us
        with the login details for your Payment Account. By providing us with
        your Payment Account login details, you confirm that you have all the
        necessary rights, permissions and authority to share your login details
        and Account Information with us, and you grant us explicit consent to
        use your login details to access your Payment Account to obtain the
        necessary Account Information on your behalf and at your direction in
        order to provide you with the Services.
      </p>
      <p>
        2.12. Once we have accessed your Account Information we may: a. share
        your Account Information with the Application through which you have
        accessed our Services; b. share your Account Information with third
        parties as directed by the Application through which you have accessed
        our Services provided, however, that you have explicitly consent to such
        sharing; or c. display your Account Information on the Plaid dashboard.
      </p>
      <p>
        2.13. You agree to us sharing the Account Information we have accessed
        from your Account Provider with the Application through which you have
        accessed our Services, and with third parties as directed by that
        Application provided that in both cases you have explicitly consented to
        such sharing. Such sharing allows the Application to provide you with
        products and services in accordance with the Application Terms.
      </p>
      <p>
        2.14. We do not check the accuracy of the Account Information retrieved
        from your Account Provider and we rely on your Account Provider to
        ensure that your Account Information is up to date and accurate.
      </p>
      <p>
        2.15. We may standardize, categorize, merge, aggregate, and otherwise
        process your Account Information before displaying your Account
        Information on the Plaid dashboard, sharing it with the Application
        through which you have accessed our Services, or sharing it with third
        parties as directed by that Application with your explicit consent.
      </p>
      <p>
        2.16. You may withdraw or vary your consent for Plaid to provide account
        information services at any time.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>
          Payment initiation services
        </span>
      </p>
      <p>
        2.17. Plaid&#39;s payment initiation service allows you to make online
        payments from your Payment Account(s). Plaid can initiate the following
        types of online payments from your Payment Account(s): (i) single
        payments; (ii) standing orders; and (iii) variable recurring payments.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>Single Payments</span>
      </p>
      <p>
        2.18. Before receiving our payment initiation service in respect of a
        single online payment order, you will have instructed the Application
        through which you have accessed our Services to make a single online
        payment using Plaid. You will be asked to review and confirm your
        payment order details for the single online payment, including: (i) the
        payment amount and (ii) the recipient details. It is your responsibility
        to ensure that all of the payment order details are correct before you
        confirm the payment order. You may not be able to recover a payment to
        an incorrect account or recipient.
      </p>
      <p>
        2.19. These details will be pre-populated for you to review and confirm.
        For example, where you use Plaid payment initiation services to purchase
        goods or services online you must ensure that the merchant recipient
        details are correct.
      </p>
      <p>
        2.20. You will confirm the payment order and provide explicit consent
        for Plaid to send the payment order to your Account Provider for
        execution, to receive information from your Account Provider on the
        initiation and execution of the payment order and to pass this
        information on to the Application through which you accessed our
        Services.
      </p>
      <p>
        2.21. When you use Plaid's payment initiation service you will be asked
        to select the Payment Account from which you will fund the payment and
        you may be redirected to your Account Provider&#39;s website or mobile
        application in order to authenticate yourself so that your Account
        Provider knows that you consent to the transaction.
      </p>
      <p>
        2.22. If the payment order relates to a transaction that is to be
        executed by your Account Provider immediately, you will not be able to
        cancel the payment order once you have provided your confirmation and
        explicit consent in accordance with paragraph 2.20.
      </p>
      <p>
        2.23. If the Application through which you accessed our Services
        supports refunds, the following provisions apply: At the time we submit
        the payment order to your Account Provider for execution, we shall ask
        the Account Provider to share your name, Payment Account sort code and
        account number or IBAN ("<strong>Account Details</strong>") with us. If
        you require a refund for the payment made using Plaid's payment
        initiation services, you must request the refund from the Application
        through which you accessed our Services. The Application will in turn
        notify us that you have requested a refund and we will share your
        Account Details with the Application in order for them to make the
        refund to your Payment Account (i.e. the Payment Account used to fund
        the original payment). The Application (and not Plaid) is responsible
        for making the refund. Plaid accepts no liability for the accuracy of
        your Account Details provided to us by your Account Provider.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>Standing Orders</span>
      </p>
      <p>
        2.24. You can use Plaid's payment initiation service to submit a
        standing order to your Account Provider. A standing order is an
        instruction to your Account Provider to make regular, recurring payments
        of the same amount to the same recipient over a specified or indefinite
        period of time.
      </p>
      <p>
        2.25. If you use Plaid's payment initiation service to submit a standing
        order you will be asked to confirm: (i) the amount of the periodic
        payment, (ii) the recipient of the payments, (iii) the payment start
        date, (iv) the interval of the payment (e.g. every week or every month)
        (v) the day on which the payment should be made (e.g. the day of the
        week or month) and (vi) the end date or that the payments will continue
        unless cancelled.
      </p>
      <p>
        2.26. Once you have confirmed the standing order details, you will be
        redirected to your bank to authenticate yourself and Plaid will submit
        the standing order instruction to your Account Provider.
      </p>
      <p>
        2.27. Once the standing order has been confirmed as received by your
        Account Provider, Plaid no longer has control over the execution of the
        standing order payments; this is the responsibility of your Account
        Provider.
      </p>
      <p>
        2.28. You will be able to view your standing order via your Account
        Provider's online banking facility (where supported). If you wish to
        change or cancel a standing order, you must do so directly with your
        Account Provider before the deadline provided by your Account Provider
        and before the next payment is due. If you miss the deadline for
        cancelling or amending your standing order, your Account Provider may
        not be able to stop the next payment in time.
      </p>
      <p>
        2.29. For further information on how you can cancel and amend standing
        orders you should refer to your Account Provider terms and conditions.
      </p>
      <p>
        <span style={{ textDecoration: 'underline' }}>
          Variable Recurring Payments
        </span>
      </p>
      <p>
        2.30. Before receiving our payment initiation service to make variable
        recurring payments, you will have instructed the Application through
        which you have accessed our Services to make a payment order for a
        series of payment transactions to the same payee using Plaid (Variable
        Recurring Payments).
      </p>
      <p>
        2.31. You will be asked to set up a mandate for the Variable Recurring
        Payments. This is your consent to Plaid to initiate a series of payments
        which fall within agreed consent parameters which are set by you, which
        may include: (i) the maximum amount per payment (ii) the frequency of
        the payments (iii) the maximum amount per frequency (e.g. no more than
        £50 per day/week/month/year) and (iv) the expiry date of the payment
        order (VRP Mandate). These details will either be pre-populated for you
        to review and confirm or you will be able to manually select your limits
        within the consent parameters. It is your responsibility to ensure that
        all of the details in the VRP Mandate are correct. You may not be able
        to recover a payment made pursuant to a VRP Mandate if any details are
        not correct to an incorrect account or recipient.
      </p>
      <p>
        2.32. You will confirm any details required in setting up the VRP
        Mandate and provide explicit consent for Plaid to: (i) send those
        details to your Account Provider; (ii) access your Payment Account;
        (iii) initiate payment transactions in accordance with the VRP Mandate;
        (iv) receive information from your Account Provider on the initiation
        and execution of your instructions; and (v) to pass this information on
        to the Application through which you accessed our Services.
      </p>
      <p>
        2.33. When you set up a VRP Mandate you will be asked to select the
        Payment Account from which you will fund the Variable Recurring Payments
        and you will be redirected to your Account Provider's website or mobile
        application in order to authenticate yourself so that your Account
        Provider knows that you consent to the VRP Mandate and to Variable
        Recurring Payments being made under the VRP Mandate.
      </p>
      <p>
        2.34. Once your Account Provider has confirmed authentication, either
        Plaid or the Application through which you accessed our Services will
        confirm to you that the VRP Mandate has been successfully set up. Plaid
        will then initiate payments under the VRP Mandate in accordance with
        payment requests made by the Application through which you have accessed
        our Services. Plaid will only initiate a payment under a VRP Mandate
        where the payment is within the parameters set by you in the VRP
        Mandate.
      </p>
      <p>
        2.35. You will be able to view and manage your VRP Mandate through your
        Account Provider, the Application and Plaid. If you wish to withdraw
        your VRP Mandate then you can do so with your Account Provider, the
        Application or Plaid directly. For further information on how you can
        withdraw your VRP Mandate through your Account Provider, you should
        refer to your Account Provider terms and conditions.
      </p>
      <p id='eligibility-end-user-uk'>
        <strong>3. Eligibility and Availability</strong>
      </p>
      <p>3.1. You can only use the Services if:</p>
      <ul>
        <li>(a) you are 18 or over;</li>
        <li>
          (b) your Payment Account and Account Provider are located in the UK;{' '}
        </li>
        <li>
          (c) you provide us with accurate, complete, and up-to-date
          information, and do not misrepresent your identity or any other
          information about you;
        </li>
        <li>
          (d) you agree to this Agreement, and to using our Services in
          accordance with this Agreement; and
        </li>
        <li>
          (e) you agree to comply with all laws and regulations applicable to
          your use of the Services.
        </li>
      </ul>
      <p>
        3.2. The Services that are available and the manner in which the
        Services are delivered may vary depending on the type of device you are
        using, the type of Payment Account(s) you have, the Account Terms and
        the Application Terms.
      </p>

      <p id='communication-end-user-uk'>
        <strong>4. Communication</strong>
      </p>
      <p>
        4.1. Where relevant, Plaid may send you information and notifications to
        your email address or mobile number via SMS where Plaid considers this
        appropriate.
      </p>
      <p>
        4.2. If your contact details (including your mobile phone number or
        email address) change, you should tell us as soon as you can. You are
        responsible for maintaining and regularly checking your device or email
        inbox for information and notifications from Plaid.
      </p>
      <p>
        4.3. You can contact Plaid by emailing us at{' '}
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>
        .
      </p>

      <p id='incorrect-unauth-end-user-uk'>
        <strong>5. Incorrect or Unauthorised Payments</strong>
      </p>
      <p>
        5.1. If you suspect that an incorrect or unauthorised payment has been
        made using Plaid's payment initiation services you must contact us as
        soon as possible by emailing{' '}
        <Link href='mailto:security@plaid.com'>
          <a>security@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        5.2. You may be entitled to a refund of the incorrect or unauthorised
        payment from your Account Provider provided that you notify them of the
        incorrect or unauthorised payment without delay and in any event no
        later than 13 months after the date of the relevant payment. You must
        contact your Account Provider in the manner set out in the applicable
        Account Terms.
      </p>
      <p>
        5.3. You should be aware that your Account Provider may contact you
        directly (and not through Plaid or the Application through which you
        have accessed our Services) if there is an issue with a payment order
        submitted through Plaid for whatever reason (for example, if there are
        insufficient funds or an issue with your authorisation). You may need to
        resolve such matters directly with your Account Provider.
      </p>

      <p id='data-end-user-uk'>
        <strong>6. Data</strong>
      </p>
      <p>
        6.1. We use your information in line with our End User Privacy Policy (“
        <strong>Privacy Policy</strong>”) which can be found on our website at{' '}
        <Link href='#end-user-privacy-policy'>
          <a>https://plaid.com/legal/#end-user-privacy-policy</a>
        </Link>
        . If you are not comfortable with how we handle your information as
        explained in the Privacy Policy, you should not use our Service.
      </p>

      <p id='liability-end-user-uk'>
        <strong>7. Liability</strong>
      </p>
      <p>
        7.1. Plaid will not be liable to refund you for any losses caused by
        circumstances beyond our control, for example, due to extreme weather,
        terrorist activity or industrial action.
      </p>
      <p>
        7.2. Plaid is not liable for any contravention of a requirement imposed
        on it by or under Part 7 of the Payment Services Regulations 2017 where
        the contravention is due to: a. abnormal or unforeseeable circumstances
        beyond Plaid&#39;s control, the consequences of which would have been
        unavoidable despite all efforts to the contrary; or b. the obligations
        of Plaid under other provisions of EU or national law.
      </p>
      <p>
        7.3. Nothing excludes or limits our liability for: a. death or personal
        injury caused by our negligence; b. our fraud or fraudulent
        misrepresentation; or c. a deliberate breach of this Agreement in a
        major way that is designed to harm you.
      </p>
      <p>
        7.4. We are not liable to you for any harm, damage or loss to you
        arising from the acts or omissions of any third parties, in particular
        your Account Provider(s) and the Application through which you have
        accessed our Services.
      </p>

      <p id='complaints-end-user-uk'>
        <strong>8. Complaints</strong>
      </p>
      <p>
        8.1. If you have a complaint about our Services, please email us at{' '}
        <Link href='mailto:regulatory@plaid.com'>
          <a>regulatory@plaid.com</a>
        </Link>{' '}
        so that Plaid can investigate the circumstances for you. We will aim to
        deal quickly and fairly with any complaints you have about our Services
        in accordance with our obligations under applicable law. Plaid may,
        however, direct you to: a. your Account Provider, if your complaint
        relates to the services provided under the Account Terms or involves an
        incorrect or unauthorised payment in accordance with paragraph 5 above;
        or b. the Application through which you accessed our Services, if your
        complaint relates to the products and/or services provided by the
        Application under the Application Terms.
      </p>
      <p>
        8.2. If your complaint relates to our Services and we do not resolve it,
        you may be able to refer it to the UK Financial Ombudsman Service. You
        can contact the UK Financial Ombudsman by telephone on: from inside the
        UK: 0300 123 9123 or 0800 023 4567; from other countries: +44 20 7964
        0500 on Monday to Friday, 8am to 8pm and on Saturday 9am to 1pm; by post
        at The Financial Ombudsman Service, Exchange Tower, London E14 9SR; or
        by email:{' '}
        <Link href='mailto:complaint.info@financial-ombudsman.org.uk'>
          <a>complaint.info@financial-ombudsman.org.uk</a>
        </Link>
        . The UK Financial Ombudsman Service is also available in a number of
        different languages and if you need it you will be put in touch with a
        translator when you contact the UK Financial Ombudsman Service.
      </p>
      <p>
        8.3. Plaid is not responsible for any complaints or disputes about
        purchases made using our payment initiation services. You should settle
        these with the person from whom you bought the goods or services. We are
        not responsible for the quality, safety, legality or any other aspect of
        any goods or services purchased using our payment initiation services.
        Remember that once you have used our payment initiation service to make
        a purchase, Plaid cannot cancel or stop that payment transaction.
      </p>
      <p>
        8.4. Plaid is also not responsible for any complaints or disputes about
        products and/or services provided by the Application through which you
        accessed our Services, other third parties, or your Account Provider.
        You should settle these with the Application, third party, or Account
        Provider directly.
      </p>

      <p id='changes-end-user-uk'>
        <strong>9. Changes to this Agreement</strong>
      </p>
      <p>
        9.1. The contract between you and Plaid which is set out in this
        Agreement and which governs your use of the Services will continue until
        cancelled in accordance with this paragraph 9.
      </p>
      <p>
        9.2. We will give you at least two months&#39; prior written notice via,
        at a minimum, the contact details you have supplied to us of any
        intended material change to this Agreement along with the new version of
        the Agreement.
      </p>
      <p>
        9.3. If you do not agree with the proposed change(s) you must tell us
        using the Plaid contact details set out in paragraph 4.3. before that
        change takes effect and you will have the right to terminate this
        Agreement at any time before the proposed date of their entry into
        force. If you do not contact us in order to tell us that you do not
        accept the changes and request to terminate this Agreement you will be
        deemed to have accepted the change(s) to the Agreement.
      </p>

      <p id='termination-end-user-uk'>
        <strong>10. Termination</strong>
      </p>
      <p>
        10.1. You have the right to cancel the contract between us, which is set
        out in this Agreement, at any time without notice by contacting Plaid
        using the Plaid contact details set out at paragraph 4.3.
      </p>
      <p>
        10.2. We may cancel the contract with you, with immediate effect, by
        giving written notice: a. if you repeatedly break this Agreement and
        fail to resolve the matter to Plaid&#39;s satisfaction in a timely
        manner; or b. in the event of your death or incapacity.
      </p>
      <p>
        10.3. We may cancel this Agreement with you for any reason by giving you
        at least 2 months&#39; written notice.
      </p>

      <p id='law-end-user-uk'>
        <strong>11. Governing law and language</strong>
      </p>
      <p>
        11.1. This Agreement is governed by English law and is subject to the
        non-exclusive jurisdiction of the English Courts.
      </p>
      <p>
        11.2. This Agreement is in English and all communications with you will
        be in English.
      </p>

      <hr className='section-divider' />
    </>
  );
};

export default UKEndUserServicesAgreement;
